import { IKuikaStyle, IStylingTypography, QuickStyleKey } from "@kuika/kuika-cl-model";
import React from "react";
import { loadFontsForPreview } from "../../../../../shared/utility/font-preview-loader";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { getOutsideStyleWriteLock } from "../../../right-panel/styling-panel/styling-panel-mode-store";
import { useStylingPanelMode } from "../use-styling-panel-mode";
import { QuickColorButton } from "./quick-color-button";
import {
  IQuickOption,
  QuickChip,
  QuickLock,
  QuickOptionList,
  QuickPopover,
  QuickStepper,
  QuickTip,
  QuickToggle,
  useQuickOpenState
} from "./quick-controls";
import { IQuickContext, warnSkippedQuickProp } from "./quick-shared";
import {
  buildQuickStyleObj,
  ITextFormat,
  QuickStylePatch,
  readTextAlign,
  readTextFormat,
  resolveFillColor,
  resolveTypography,
  STYLE_LABELS,
  TextAlignValue,
  TextFormatKey
} from "./quick-style-model";
import { commitQuickStyle } from "./quick-writes";

export interface IQuickStyleControlProps {
  ctx: IQuickContext;
  styleKey: QuickStyleKey;
}

interface IFormatToggle {
  format: TextFormatKey;
  label: string;
  glyph: string;
}

const FORMAT_TOGGLES: readonly IFormatToggle[] = [
  { format: "bold", label: "Bold", glyph: "B" },
  { format: "italic", label: "Italic", glyph: "I" },
  { format: "underline", label: "Underline", glyph: "U" }
];

interface IAlignToggle {
  value: TextAlignValue;
  label: string;
  icon: string;
}

const ALIGN_TOGGLES: readonly IAlignToggle[] = [
  { value: "left", label: "Align left", icon: "align-left" },
  { value: "center", label: "Align center", icon: "align-center" },
  { value: "right", label: "Align right", icon: "align-right" }
];

/**
 * One styling-panel field (text / fill / alignment), read from and written to the component's styleObj.
 * Disabled while the styling panel shows this component in a state or tag-edit mode: the panel restores
 * or commits its own copy of the styleObj when that mode ends, which would drop a write made here.
 */
export const QuickStyleControl = ({ ctx, styleKey }: IQuickStyleControlProps): JSX.Element => {
  const lockReason: string | null = getOutsideStyleWriteLock(useStylingPanelMode(), ctx.kuikaId);
  return (
    <QuickLock reason={lockReason} testId={`quickProp-style-${styleKey}-locked`}>
      <QuickStyleField ctx={ctx} styleKey={styleKey} />
    </QuickLock>
  );
};

const QuickStyleField = ({ ctx, styleKey }: IQuickStyleControlProps): JSX.Element | null => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  const style: IKuikaStyle | undefined = ctx.displayItem.styleObj;
  const typography: IStylingTypography = resolveTypography(style, ctx.sd.project ?? {});
  const label: string = STYLE_LABELS[styleKey];
  const testId: string = `quickProp-style-${styleKey}`;
  // Reads the LIVE styleObj at write time, not the render's: a debounced stepper / colour write fires
  // after the edits made inside its window (Bold, a style tag, an undo) and must patch them, not revert them.
  const commit = (patch: QuickStylePatch): void => {
    commitQuickStyle(ctx.kuikaId, (live: IKuikaStyle | undefined): IKuikaStyle => buildQuickStyleObj(live, patch));
  };

  switch (styleKey) {
    case "text.fontSize":
      return (
        <QuickTip label={label}>
          <span>
            <QuickStepper
              value={typeof typography.fontSize === "number" ? typography.fontSize : null}
              ariaLabel={label}
              testId={testId}
              keyLabel="Size"
              min={1}
              onCommit={(value: number): void => commit({ key: "text.fontSize", value })}
            />
          </span>
        </QuickTip>
      );
    case "text.fontFamily": {
      const fonts: string[] = ctx.sd.project?.componentSetObj?.fontNames ?? [];
      const current: string | undefined = typography.fontFamily || undefined;
      return (
        <QuickPopover
          open={isOpen}
          onOpenChange={setIsOpen}
          testId={`quickPropPopover-style-${styleKey}`}
          content={
            <QuickOptionList
              title="Font"
              options={fonts.map((font: string): IQuickOption => ({
                value: font,
                label: font,
                labelStyle: { fontFamily: font }
              }))}
              current={current}
              isSearchable
              optionTestId={(o: IQuickOption): string => `quickPropOption-fontFamily-${o.value}`}
              onPick={(value: string): void => {
                setIsOpen(false);
                commit({ key: "text.fontFamily", value });
              }}
            />
          }
        >
          <QuickTip label={label} isSuppressed={isOpen}>
            <QuickChip
              testId={testId}
              ariaLabel={label}
              icon="letter-case"
              iconType="styling-panel-icons"
              value={current ?? "Default font"}
              isEmpty={!current}
              valueStyle={current ? { fontFamily: current } : undefined}
              hasPopup
              isOpen={isOpen}
              onClick={(): void => {
                if (!isOpen) {
                  loadFontsForPreview(fonts);
                }
                setIsOpen(!isOpen);
              }}
            />
          </QuickTip>
        </QuickPopover>
      );
    }
    case "text.format": {
      const format: ITextFormat = readTextFormat(typography);
      return (
        <span className="kuika-quick-segmented" role="group" aria-label={label} data-testid={testId}>
          {FORMAT_TOGGLES.map((toggle: IFormatToggle): JSX.Element => (
            <QuickTip key={toggle.format} label={toggle.label}>
              <QuickToggle
                isOn={format[toggle.format]}
                label={toggle.label}
                testId={`${testId}-${toggle.format}`}
                onToggle={(): void =>
                  commit({ key: "text.format", format: toggle.format, isOn: !format[toggle.format] })
                }
              >
                <span className={`kuika-quick-toggle__glyph--${toggle.format}`} aria-hidden="true">
                  {toggle.glyph}
                </span>
              </QuickToggle>
            </QuickTip>
          ))}
        </span>
      );
    }
    case "text.color":
      return (
        <QuickColorButton
          color={typography.color ?? null}
          label={label}
          testId={testId}
          onCommit={(value: string): void => commit({ key: "text.color", value })}
        />
      );
    case "fill.color":
      return (
        <QuickColorButton
          color={resolveFillColor(style, ctx.sd.project ?? {})}
          label={label}
          testId={testId}
          onCommit={(value: string): void => commit({ key: "fill.color", value })}
        />
      );
    case "position.textAlign": {
      const current: TextAlignValue | null = readTextAlign(style);
      return (
        <span className="kuika-quick-segmented" role="group" aria-label={label} data-testid={testId}>
          {ALIGN_TOGGLES.map((toggle: IAlignToggle): JSX.Element => (
            <QuickTip key={toggle.value} label={toggle.label}>
              <QuickToggle
                isOn={current === toggle.value}
                label={toggle.label}
                testId={`${testId}-${toggle.value}`}
                onToggle={(): void => commit({ key: "position.textAlign", value: toggle.value })}
              >
                <KuikaSvg
                  name={toggle.icon}
                  type="styling-panel-icons"
                  width={16}
                  height={16}
                  color="currentColor"
                  aria-hidden="true"
                />
              </QuickToggle>
            </QuickTip>
          ))}
        </span>
      );
    }
    default: {
      // A style key from a newer component set: skip the control rather than fail the whole bar.
      const unhandled: never = styleKey;
      warnSkippedQuickProp(undefined, "unknownStyleKey", unhandled);
      return null;
    }
  }
};
